import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormData } from "../../context";
import ConstituencySearch from '../inputs/ConstituencySearch';
import TemplateEditor from '../inputs/TemplateEditor';
import NavigationStep from './NavigationStep';
import Confirmation from "../inputs/Confirmation";
import { storePurpose, providePurpose } from "../../utils/Purpose";
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from "yup";
import { bogusCheck } from '../../utils/ValChecker';
import { env } from '../../env'

function ConstituencyTemplateStep(props) {
    // Declare state variables using useForm
    const { purposeId, session, provider, route, nextRoute, prevRoute, isMultiStep } = props;
    const { setFormValues, data } = useFormData();

    const navigate = useNavigate();
    const { pathname, search } = useLocation();

    const [templateNeedsChanging, setTemplateNeedsChanging] = useState(false);
    const [replacementValues, setReplacementValues] = useState([]);
    const [showTemplate, setShowTemplate] = useState(false);

    Yup.addMethod(Yup.string, "templateCheck", function (errorMessage) {
        return this.test(`templateCheck`, errorMessage, function (value) {
            const { path, createError } = this;

            return (
                bogusCheck(value) === true ||
                createError({ path, message: errorMessage })
            );
        });
    });

    const validationSchema = Yup.object({
        template: Yup.string().required("* Letter content is required, click reset to retrieve original template").templateCheck("* Invalid text found in content"),
        constituency: Yup.object({ id: Yup.number().required("* Constituency is required").positive().integer() }),
        agreement: Yup.boolean().oneOf([true])
    });

    const { handleSubmit, formState, control, setValue, getValues, watch, resetField } = useForm(
        {
            mode: "onSubmit",
            resolver: yupResolver(validationSchema),
            defaultValues: {
                constituency: providePurpose(`df.${session.formId}.const`, purposeId),
                agreement: providePurpose('df.ag', purposeId),
                address: providePurpose('df.ad', purposeId),
                user: providePurpose('df.us', purposeId),
            }
        });

    const { errors } = formState;

    // Actual Values
    const watchconstituency = useWatch({ control, name: 'constituency', defaultValue: providePurpose(`df.${session.formId}.const`, purposeId) });
    const watchuser = useWatch({ control, name: 'user', defaultValue: providePurpose('df.us', purposeId) });
    const watchaddress = useWatch({ control, name: 'address', defaultValue: providePurpose('df.ad', purposeId) });
    // Update Events
    const watchconstituencyselected = watch('constituencySelected', false);
    const watchconstituencychange = watch('constituencyChange', false);

    // Declare effects using useEffect
    useEffect(() => {
        document.title = 'CPA - Letter';

        if (isMultiStep && (!watchuser || !watchaddress)) {
            navigate({ pathname: prevRoute, replace: false, preserveSearch: true, search });
        } 
    }, []);

    useEffect(() => {
        if (env.VITE_ENV && env.VITE_ENV.toLowerCase() !== 'production') {
            console.log(`ConstituencyTemplateStep.useEffect.templateUpdate :: constituencyselected: ${watchconstituencyselected} constituencyChange: ${watchconstituencychange} userChange: ${data.userChange}`);
        }

        if (watchconstituencychange === true || data.userChange === true) {
            handleReset();
        } else if (watchconstituencyselected && showTemplate === false) {
            handleTemplateChange();
        } 

    }, [watchconstituencyselected, watchconstituencychange, data.userChange]);

    // Declare event handlers or other functions
    const handleTemplateChange = () => {
        if (watchconstituency !== null && watchuser !== null) {
            setReplacementValues([
                { key: '{CONSTITUENCY_GREETING_NAME}', value: watchconstituency.memberName },
                { key: '{USER_DETAILS_TITLE}', value: watchuser.title === 'Other' ? watchuser.otherTitle : watchuser.title || '' },
                { key: '{USER_DETAILS_FIRSTNAME}', value: watchuser.firstName || '' },
                { key: '{USER_DETAILS_LASTNAME}', value: watchuser.lastName || '' }
            ]);
            setShowTemplate(true);
        } else {
            setShowTemplate(false);
        }
    };

    const handleReset = () => {
        resetField("template");
        localStorage.removeItem(`df.${session.templateId}.let`);
        setValue('constituencySelected', false);
        setValue('constituencyChange', false);
        setFormValues({ userChange: false });
        setTemplateNeedsChanging(true);
        handleTemplateChange();
    }

    const onSubmit = (args) => {
        if (env.VITE_ENV && env.VITE_ENV.toLowerCase() !== 'production') {
            console.log(`ConstituencyTemplateStep.onSubmit`);
            console.log(args);
        }

        if (args.template && args.constituency && args.agreement) {
            args.isFormCompleted = true;
            setFormValues(args);
            storePurpose(`df.${session.formId}.const`, args.constituency, purposeId);
            storePurpose(`df.${session.templateId ?? session.template.templateId}.let`, args.template , purposeId);
            storePurpose('df.ag', args.agreement , purposeId);
            navigate({ pathname: nextRoute, replace: false, preserveSearch: true, search });
        }
    };

    return (
        <div style={pathname === route ? {} : { display: 'none' }}>

            <form onSubmit={handleSubmit(onSubmit)}>

                <div className="col-xs-12">
                    <ConstituencySearch purposeId={purposeId} provider={provider} formId={session.formId} setValue={setValue} formSettings={session.settings} getValues={getValues} address={watchaddress} />
                </div>

                {showTemplate && watchconstituency && (
                    <div>
                        <div className="col-xs-12">
                            <TemplateEditor purposeId={purposeId} formId={session.formId} provider={provider} formTemplateId={session.templateId ?? session.template.templateId} setValue={setValue} errors={errors} replacementValues={replacementValues} templateNeedsChanging={templateNeedsChanging} allowEditing={session.settings.allowTemplateEditing} />
                        </div>
                        <div className="col-xs-12">
                            <Confirmation control={control} errors={errors} tenant={session.tenant} />
                        </div>
                    </div>)}
         
                <div className="col-xs-12">
                    <NavigationStep prevRoute={prevRoute} showReset={showTemplate && session.settings.allowTemplateEditing} showNext={showTemplate} handleReset={handleReset} nextLabel="Submit" />
                </div>
            </form>
        </div>
    );
} export default ConstituencyTemplateStep;